import { useState } from 'react';
import { SCENARIOS } from '../physics/scenarios';
import { SavedWorld } from '../lib/storage';

interface Props {
  running: boolean;
  setRunning: (v: boolean) => void;
  timeScale: number;
  setTimeScale: (v: number) => void;
  showPredictions: boolean;
  setShowPredictions: (v: boolean) => void;
  energy: number;
  bodyCount: number;
  saves: SavedWorld[];
  onLoadScenario: (index: number) => void;
  onClear: () => void;
  onSave: (name: string) => void;
  onLoadSave: (save: SavedWorld) => void;
  onDeleteSave: (index: number) => void;
  selectedId: number | null;
  onRemoveBody: (id: number) => void;
}

export default function ControlPanel({
  running,
  setRunning,
  timeScale,
  setTimeScale,
  showPredictions,
  setShowPredictions,
  energy,
  bodyCount,
  saves,
  onLoadScenario,
  onClear,
  onSave,
  onLoadSave,
  onDeleteSave,
  selectedId,
  onRemoveBody,
}: Props) {
  const [saveName, setSaveName] = useState('');
  const [showSaves, setShowSaves] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  return (
    <>
      {/* Top bar */}
      <div className="panel top-panel">
        <h1>N-Body Sandbox</h1>
        <div className="stats">
          <span>{bodyCount} bodies</span>
          <span>E: {energy.toFixed(1)}</span>
        </div>
        <div className="btn-group">
          <button onClick={() => setRunning(!running)} className="btn primary">
            {running ? '⏸ Pause' : '▶ Play'}
          </button>
          <button onClick={onClear} className="btn">Clear</button>
          <button onClick={() => setShowHelp(!showHelp)} className="btn">?</button>
        </div>
      </div>

      {/* Left panel */}
      <div className="panel left-panel">
        <div className="section">
          <label>Time Scale</label>
          <input
            type="range"
            min="0"
            max="5"
            step="0.1"
            value={timeScale}
            onChange={(e) => setTimeScale(parseFloat(e.target.value))}
          />
          <span className="value">{timeScale.toFixed(1)}x</span>
        </div>

        <div className="section">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={showPredictions}
              onChange={(e) => setShowPredictions(e.target.checked)}
            />
            Show Predictions
          </label>
        </div>

        <div className="section">
          <label>Scenarios</label>
          <div className="scenario-list">
            {SCENARIOS.map((s, i) => (
              <button key={s.name} onClick={() => onLoadScenario(i)} className="scenario-btn">
                {s.name}
              </button>
            ))}
          </div>
        </div>

        {selectedId !== null && (
          <div className="section">
            <label>Selected Body</label>
            <button onClick={() => onRemoveBody(selectedId)} className="btn danger">
              Delete Body
            </button>
          </div>
        )}
      </div>

      {/* Bottom right */}
      <div className="panel bottom-right-panel">
        <div className="section">
          <label>Saves</label>
          <div className="save-row">
            <input
              type="text"
              placeholder="Name..."
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && saveName.trim()) {
                  onSave(saveName.trim());
                  setSaveName('');
                }
              }}
            />
            <button onClick={() => { if (saveName.trim()) { onSave(saveName.trim()); setSaveName(''); } }} className="btn small">
              Save
            </button>
          </div>
          <button onClick={() => setShowSaves(!showSaves)} className="btn small">
            {showSaves ? 'Hide' : `Load (${saves.length})`}
          </button>
          {showSaves && (
            <div className="save-list">
              {saves.length === 0 && <span className="empty">No saves</span>}
              {saves.map((s, i) => (
                <div key={i} className="save-item">
                  <button onClick={() => onLoadSave(s)} className="save-name">
                    {s.name}
                  </button>
                  <button onClick={() => onDeleteSave(i)} className="delete-btn">×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Help overlay */}
      {showHelp && (
        <div className="help-overlay" onClick={() => setShowHelp(false)}>
          <div className="help-content" onClick={(e) => e.stopPropagation()}>
            <h2>Controls</h2>
            <ul>
              <li><strong>Drag</strong> on empty space to launch a new body</li>
              <li><strong>Click</strong> a body to select it</li>
              <li><strong>Shift+Drag</strong> or <strong>Middle-click</strong> to pan the camera</li>
              <li><strong>Scroll</strong> to zoom in/out</li>
              <li>Use the <strong>Time Scale</strong> slider to speed up or slow down</li>
              <li>Load <strong>Scenarios</strong> for preset systems</li>
              <li><strong>Save</strong> your creations to localStorage</li>
            </ul>
            <button onClick={() => setShowHelp(false)} className="btn primary">Got it</button>
          </div>
        </div>
      )}
    </>
  );
}
